
import { mapGetters, mapState } from 'vuex'
import markerSDK from '@marker.io/browser'
import Header from '../components/common/Header.vue'
import Toast from '../components/common/Toast.vue'
import Sidebar from '@/components/common/Sidebar'
import BottomNav from '@/components/common/BottomNav'
import ProfileModal from '@/components/modals/Profile'
import Global from '@/mixins/Global'

export default {
  components: {
    Sidebar,
    BottomNav,
    Header,
    ProfileModal,
    Toast,
  },
  mixins: [Global],
  data () {
    return {
      windowWidth: null,
      showingProfileModal: false,
    }
  },
  computed: {
    ...mapState('lookups', ['pastoralMapping']),
    ...mapGetters('profile', ['pastoralRoleId', 'isLeader']),
  },
  watch: {
    windowWidth (width) {
      if (width >= 1024) {
        this.showingProfileModal = false
      }
    },
    showingProfileModal (isShowing) {
      if (isShowing) {
        document.body.classList.add('overflow-hidden')
      } else {
        document.body.classList.remove('overflow-hidden')
      }
    },
    pastoralMapping (pastoralMapping) {
      if (this.pastoralRoleId || this.isLeader) {
        const isLdr = pastoralMapping[this.pastoralRoleId] >= 6 || this.isLeader
        this.$store.dispatch('profile/updateIsLeader', isLdr)
        this.$store.dispatch('calendar/updateIsLeadersCalendar', isLdr)
      }
    },
    pastoralRoleId (pastoralRoleId) {
      if (this.pastoralMapping || this.isLeader) {
        const isLdr = this.pastoralMapping[pastoralRoleId] >= 6 || this.isLeader
        this.$store.dispatch('profile/updateIsLeader', isLdr)
        this.$store.dispatch('calendar/updateIsLeadersCalendar', isLdr)
      }
    },
  },
  async created () {
    // Skip auth check for auth pages - let auth middleware handle those
    const authPages = ['/auth/login/', '/auth/callback/', '/auth/logout/', '/auth/unavailable/']
    const currentPath = this.$route.path
    
    if (authPages.includes(currentPath)) {
      console.log('[Layout] On auth page, skipping auth check')
      return
    }

    // Check if user is authenticated before attempting to fetch profile
    // In PWA standalone mode, auth might not be initialized immediately
    const checkAuthAndFetch = async () => {
      // Wait for auth module to be ready (with timeout)
      const authReady = await Promise.race([
        new Promise(resolve => {
          if (this.$auth) {
            resolve(true)
          } else {
            // Wait up to 3 seconds for auth to initialize (increased from 2s)
            const checkInterval = setInterval(() => {
              if (this.$auth) {
                clearInterval(checkInterval)
                resolve(true)
              }
            }, 100)
            setTimeout(() => {
              clearInterval(checkInterval)
              resolve(false)
            }, 3000)
          }
        }),
        new Promise(resolve => setTimeout(() => resolve(false), 3000)),
      ])

      if (!authReady) {
        console.warn('[Layout] Auth module not ready after timeout')
        // Don't redirect here - let the auth middleware handle it
        // The auth middleware will redirect if needed
        return
      }

      // Check if user is logged in
      // Give auth state a moment to settle after callback redirect
      // Wait a bit longer if we just came from callback
      const cameFromCallback = document.referrer.includes('/auth/callback') || 
                               sessionStorage.getItem('justLoggedIn') === 'true' ||
                               localStorage.getItem('justLoggedIn') === 'true'
      
      if (cameFromCallback) {
        // Wait a bit longer for auth state to settle
        await new Promise(resolve => setTimeout(resolve, 800))
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem('justLoggedIn')
        }
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('justLoggedIn')
        }
      }

      if (!this.$auth.loggedIn) {
        console.log('[Layout] User not logged in')
        // Don't redirect here - let the auth middleware handle it
        // The auth middleware will redirect if needed
        return
      }

      // User is authenticated, fetch profile and lookups
      try {
        await Promise.all([
          this.$store.dispatch('profile/fetchProfile'),
          this.$store.dispatch('lookups/fetchIfNotYet'),
        ])
      } catch (error) {
        console.error('[Layout] Failed to fetch profile or lookups:', error)
        
        // If it's an auth error (401), don't redirect here - let auth middleware handle it
        if (error.response?.status === 401) {
          console.log('[Layout] Unauthorized - auth middleware will handle redirect')
          return
        }
        
        // For other errors, continue (user can still use the app)
        console.warn('[Layout] Non-auth error, continuing...')
      }
    }

    await checkAuthAndFetch()

    if (process.env.NODE_ENV === 'staging') {
      try {
        const widget = await markerSDK.loadWidget({
          destination: process.env.MARKERIO_DESTINATION, // <- Your unique destination ID
        })
        widget.setReporter({ email: this.email, fullName: this.name })
      } catch (error) {
        console.warn('[Layout] Failed to load marker widget:', error)
      }
    }
  },
}
